import React, { useRef, useEffect, useState } from "react";
import useGameBase from "../../hooks/useGameBase";
import Modal from "../../components/common/Modal";
import { useSound } from '../../utils/sound/SoundProvider';

function CanvasCrazeGame({ gameState, gamePlayers, user, socket, roomId }) {
        const sound = useSound();
        // Track previous correct guessers length for sound effect
        const prevCorrectGuessersRef = useRef((gameState.correctGuessers || []).length);

        useEffect(() => {
          const prevLen = prevCorrectGuessersRef.current;
          const currLen = (gameState.correctGuessers || []).length;
          if (currLen > prevLen) {
            // Play positive sound for new correct guess
            sound && sound.play && sound.play('ui:positive');
          }
          prevCorrectGuessersRef.current = currLen;
        }, [gameState.correctGuessers, sound]);
      const [showBrushDropdown, setShowBrushDropdown] = useState(false);
      const [showShapeDropdown, setShowShapeDropdown] = useState(false);
      // Brush size options: only circles, spanning the slider range
      const brushSizes = [1, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32].map(v => ({
        value: v,
        svg: (
          <svg width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r={Math.max(1, v/2)} fill="#333" className="dark:fill-gray-100" />
          </svg>
        )
      }));
    // Undo/redo, eraser, and fill removed as requested
  const { submitAction } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
  });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState(() => normalizeHexColor("#007bffff"));
  const [size, setSize] = useState(4);
  const [brushType, setBrushType] = useState("round");
  const [localStroke, setLocalStroke] = useState(null);
  const [guess, setGuess] = useState("");
  const [visibleGuesses, setVisibleGuesses] = useState({});

  const [now, setNow] = useState(Date.now());
  // For clear modal
  const [showClearModal, setShowClearModal] = useState(false);
  // Undo/redo is now server-driven; no local history/redoStack
  // For shape drawing
  // Removed unused shapeStart state
  const [shapePreview, setShapePreview] = useState(null); // {type, start, end, color, size}
  const canvasBackground = normalizeHexColor(gameState.canvasBackground || "#ffffff");
  // For color palette
  // Removed unused recentColors state

  // when drawer needs to choose a prompt
  const handleChoosePrompt = (choice, index) => {
    // send the index to the server; server will map to word
    submitAction({ type: "choosePrompt", index });
  };

  const isDrawer = user.socketId === gameState.drawer;
  // Resolve the drawer player's display name for UI (fallback to 'Drawer')
  const drawerPlayer = (gamePlayers || []).find(
    (p) => p.socketId === gameState.drawer || p.id === gameState.drawer
  );
  const drawerName =
    (drawerPlayer &&
      (drawerPlayer.username ||
        drawerPlayer.name ||
        drawerPlayer.displayName)) ||
    "Drawer";
  const promptChoices = gameState.promptChoices || [];
  // Show prompt UI if server explicitly set phase, or if choices exist and no currentWord yet
  const showPrompt = isDrawer && (gameState.phase === "choosePrompt" || (promptChoices.length > 0 && !gameState.currentWord));
  const waitingOnDrawerPicking = !isDrawer && (gameState.phase === "choosePrompt" || (promptChoices.length > 0 && !gameState.currentWord));

  // Always show crosshair for all drawing tools (ink and shapes) when drawer and in draw phase
  useEffect(() => {
    if (!canvasRef.current) return;
    if (isDrawer && gameState.phase === "draw") {
      canvasRef.current.style.cursor = 'crosshair';
    } else {
      canvasRef.current.style.cursor = 'not-allowed';
    }
  }, [isDrawer, gameState.phase, brushType]);

  // Unified render: draw all strokes (freehand and shapes) from gameState.drawingStrokes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = canvasBackground;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const strokes = gameState.drawingStrokes || [];
    for (const s of strokes) {
      if (Array.isArray(s.points) && s.points.length > 0) {
        // Freehand stroke only (eraser removed)
        const brush = s.brush || "round";
        if (brush === "spray") {
          ctx.fillStyle = s.color || "#003cffff";
          for (const p of s.points) {
            const cx = p.x * canvas.width;
            const cy = p.y * canvas.height;
            const dots = Math.max(30, (s.size || 4) * 12);
            for (let d = 0; d < dots; d++) {
              const angle = Math.random() * Math.PI * 2;
              const radius = Math.random() * (s.size || 4);
              const x = cx + Math.cos(angle) * radius;
              const y = cy + Math.sin(angle) * radius;
              ctx.fillRect(x, y, 1, 1);
            }
          }
        } else {
          ctx.beginPath();
          ctx.lineJoin = brush === "square" ? "miter" : "round";
          ctx.lineCap = brush === "square" ? "butt" : "round";
          ctx.strokeStyle = s.color || "#003cffff";
          ctx.lineWidth = s.size || 4;
          ctx.globalAlpha = brush === "marker" ? 0.45 : 1;
          ctx.moveTo(s.points[0].x * canvas.width, s.points[0].y * canvas.height);
          for (let i = 1; i < s.points.length; i++)
            ctx.lineTo(s.points[i].x * canvas.width, s.points[i].y * canvas.height);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      } else if (s.type && s.start && s.end) {
        // Shape stroke
        ctx.save();
        ctx.strokeStyle = s.color;
        ctx.lineWidth = s.size;
        const x0 = s.start.x * canvas.width;
        const y0 = s.start.y * canvas.height;
        const x1 = s.end.x * canvas.width;
        const y1 = s.end.y * canvas.height;
        switch (s.type) {
          case "rectangle":
            ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
            break;
          case "rectangle_filled":
            ctx.fillStyle = s.color;
            ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
            ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
            break;
          case "circle": {
            const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
            ctx.beginPath();
            ctx.arc(x0, y0, r, 0, 2 * Math.PI);
            ctx.stroke();
            break;
          }
          case "circle_filled": {
            const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
            ctx.beginPath();
            ctx.arc(x0, y0, r, 0, 2 * Math.PI);
            ctx.fillStyle = s.color;
            ctx.fill();
            ctx.stroke();
            break;
          }
          case "oval":
            ctx.beginPath();
            ctx.ellipse(
              (x0 + x1) / 2,
              (y0 + y1) / 2,
              Math.abs(x1 - x0) / 2,
              Math.abs(y1 - y0) / 2,
              0,
              0,
              2 * Math.PI
            );
            ctx.stroke();
            break;
          case "oval_filled":
            ctx.beginPath();
            ctx.ellipse(
              (x0 + x1) / 2,
              (y0 + y1) / 2,
              Math.abs(x1 - x0) / 2,
              Math.abs(y1 - y0) / 2,
              0,
              0,
              2 * Math.PI
            );
            ctx.fillStyle = s.color;
            ctx.fill();
            ctx.stroke();
            break;
          case "line":
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.stroke();
            break;
          case "arrow": {
            // // Arrowhead length scales with brush size (default 18, add 1.2*size if size > 8)
            // const dx = x1 - x0;
            // const dy = y1 - y0;
            // const len = Math.sqrt(dx * dx + dy * dy);
            // const baseHeadlen = 18;
            // const headlen = baseHeadlen + (s.size > 8 ? (s.size * 1.2) : 0);
            // const angle = Math.atan2(dy, dx);
            // // Move the endpoint forward so the arrowhead sits off the end
            // const x1a = x1 + (headlen * 0.2) * Math.cos(angle);
            // const y1a = y1 + (headlen * 0.2) * Math.sin(angle);
            // ctx.beginPath();
            // ctx.moveTo(x0, y0);
            // ctx.lineTo(x1a, y1a);
            // ctx.stroke();
            // if (len > 0) {
            //   const ah1 = angle - Math.PI / 7;
            //   const ah2 = angle + Math.PI / 7;
            //   ctx.beginPath();
            //   ctx.moveTo(x1a, y1a);
            //   ctx.lineTo(x1a - headlen * Math.cos(ah1), y1a - headlen * Math.sin(ah1));
            //   ctx.lineTo(x1a - headlen * Math.cos(ah2), y1a - headlen * Math.sin(ah2));
            //   ctx.closePath();
            //   ctx.fillStyle = s.color;
            //   ctx.globalAlpha = 0.3;
            //   ctx.fill();
            //   ctx.globalAlpha = 0.7;
            // }
            // break;
            const baseHeadlen = 18;
            const headlen = baseHeadlen + (s.size > 8 ? (s.size * 1.2) : 0);
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.stroke();
            // Arrowhead
            const dx = x1 - x0;
            const dy = y1 - y0;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len > 0) {
              
              const angle = Math.atan2(dy, dx);
              const ah1 = angle - Math.PI / 7;
              const ah2 = angle + Math.PI / 7;
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.lineTo(x1 - headlen * Math.cos(ah1), y1 - headlen * Math.sin(ah1));
              ctx.lineTo(x1 - headlen * Math.cos(ah2), y1 - headlen * Math.sin(ah2));
              ctx.closePath();
              ctx.fillStyle = s.color;
              ctx.globalAlpha = 1.0;
              ctx.fill();
              ctx.globalAlpha = 1.0;
            }
            ctx.stroke();
            break;
            }
          
          case "hexagon":
          case "hexagon_filled": {
            const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
            const rx = Math.abs(x1 - x0) / 2, ry = Math.abs(y1 - y0) / 2;
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              const angle = Math.PI / 3 * i - Math.PI / 6;
              const px = cx + rx * Math.cos(angle);
              const py = cy + ry * Math.sin(angle);
              if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.closePath();
            if (s.type === "hexagon_filled") {
              ctx.fillStyle = s.color;
              ctx.fill();
            }
            ctx.stroke();
            break;
          }
          case "triangle":
            ctx.beginPath();
            ctx.moveTo((x0 + x1) / 2, y0);
            ctx.lineTo(x1, y1);
            ctx.lineTo(x0, y1);
            ctx.closePath();
            ctx.stroke();
            break;
          case "triangle_filled":
            ctx.beginPath();
            ctx.moveTo((x0 + x1) / 2, y0);
            ctx.lineTo(x1, y1);
            ctx.lineTo(x0, y1);
            ctx.closePath();
            ctx.fillStyle = s.color;
            ctx.fill();
            ctx.stroke();
            break;
          case "pentagon": {
            const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
            const r = Math.min(Math.abs(x1 - x0), Math.abs(y1 - y0)) / 2;
            ctx.beginPath();
            for (let i = 0; i < 5; i++) {
              const angle = Math.PI / 2 + (2 * Math.PI * i) / 5;
              const px = cx + r * Math.cos(angle);
              const py = cy + r * Math.sin(angle);
              if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.stroke();
            break;
          }
          case "pentagon_filled": {
            const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
            const r = Math.min(Math.abs(x1 - x0), Math.abs(y1 - y0)) / 2;
            ctx.beginPath();
            for (let i = 0; i < 5; i++) {
              const angle = Math.PI / 2 + (2 * Math.PI * i) / 5;
              const px = cx + r * Math.cos(angle);
              const py = cy + r * Math.sin(angle);
              if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.fillStyle = s.color;
            ctx.fill();
            ctx.stroke();
            break;
          }
          case "star": {
            const drawStar = (cx, cy, spikes, outerRadius, innerRadius, filled = false) => {
              let rot = Math.PI / 2 * 3;
              let x = cx;
              let y = cy;
              const step = Math.PI / spikes;
              ctx.beginPath();
              ctx.moveTo(cx, cy - outerRadius);
              for (let i = 0; i < spikes; i++) {
                x = cx + Math.cos(rot) * outerRadius;
                y = cy + Math.sin(rot) * outerRadius;
                ctx.lineTo(x, y);
                rot += step;
                x = cx + Math.cos(rot) * innerRadius;
                y = cy + Math.sin(rot) * innerRadius;
                ctx.lineTo(x, y);
                rot += step;
              }
              ctx.lineTo(cx, cy - outerRadius);
              ctx.closePath();
              if (filled) {
                ctx.fillStyle = s.color;
                ctx.fill();
              } else {
                ctx.stroke();
              }
            };
            const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
            drawStar(x0, y0, 5, r, r / 2.5);
            break;
          }
          case "star_filled": {
            const drawStar = (cx, cy, spikes, outerRadius, innerRadius, filled = false) => {
              let rot = Math.PI / 2 * 3;
              let x = cx;
              let y = cy;
              const step = Math.PI / spikes;
              ctx.beginPath();
              ctx.moveTo(cx, cy - outerRadius);
              for (let i = 0; i < spikes; i++) {
                x = cx + Math.cos(rot) * outerRadius;
                y = cy + Math.sin(rot) * outerRadius;
                ctx.lineTo(x, y);
                rot += step;
                x = cx + Math.cos(rot) * innerRadius;
                y = cy + Math.sin(rot) * innerRadius;
                ctx.lineTo(x, y);
                rot += step;
              }
              ctx.lineTo(cx, cy - outerRadius);
              ctx.closePath();
              ctx.fillStyle = s.color;
              ctx.fill();
              ctx.stroke();
            };
            const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
            drawStar(x0, y0, 5, r, r / 2.5, true);
            break;
          }
          default:
            break;
        }
        ctx.restore();
      }
    }
    // Draw current local stroke (for smoother UX)
    if (localStroke && localStroke.points && localStroke.points.length > 0) {
      const s = localStroke;
      const brush = s.brush || "round";
      if (brush === "spray") {
        ctx.fillStyle = s.color || "#003cffff";
        for (const p of s.points) {
          const cx = p.x * canvas.width;
          const cy = p.y * canvas.height;
          const dots = Math.max(30, (s.size || 4) * 12);
          for (let d = 0; d < dots; d++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * (s.size || 4);
            const x = cx + Math.cos(angle) * radius;
            const y = cy + Math.sin(angle) * radius;
            ctx.fillRect(x, y, 1, 1);
          }
        }
      } else {
        ctx.beginPath();
        ctx.lineJoin = brush === "square" ? "miter" : "round";
        ctx.lineCap = brush === "square" ? "butt" : "round";
        ctx.strokeStyle = s.color || "#003cffff";
        ctx.lineWidth = s.size || 4;
        ctx.globalAlpha = brush === "marker" ? 0.45 : 1;
        ctx.moveTo(s.points[0].x * canvas.width, s.points[0].y * canvas.height);
        for (let i = 1; i < s.points.length; i++)
          ctx.lineTo(
            s.points[i].x * canvas.width,
            s.points[i].y * canvas.height
          );
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }
    // Draw shape preview if present (for smoother UX)
    if (shapePreview && shapePreview.start && shapePreview.end) {

    
      ctx.save();
      ctx.strokeStyle = shapePreview.color;
      ctx.lineWidth = shapePreview.size;
      ctx.globalAlpha = 0.7;
      const { start, end, type } = shapePreview;
      const x0 = start.x * canvas.width;
      const y0 = start.y * canvas.height;
      const x1 = end.x * canvas.width;
      const y1 = end.y * canvas.height;
      switch (type) {
        case "rectangle":
          ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
          break;
        case "rectangle_filled":
          ctx.fillStyle = shapePreview.color;
          ctx.globalAlpha = 0.3;
          ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
          ctx.globalAlpha = 0.7;
          ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
          break;
        case "circle": {
          const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
          ctx.beginPath();
          ctx.arc(x0, y0, r, 0, 2 * Math.PI);
          ctx.stroke();
          break;
        }
        case "circle_filled": {
          const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
          ctx.beginPath();
          ctx.arc(x0, y0, r, 0, 2 * Math.PI);
          ctx.fillStyle = shapePreview.color;
          ctx.globalAlpha = 0.3;
          ctx.fill();
          ctx.globalAlpha = 0.7;
          ctx.stroke();
          break;
        }
        case "oval":
          ctx.beginPath();
          ctx.ellipse(
            (x0 + x1) / 2,
            (y0 + y1) / 2,
            Math.abs(x1 - x0) / 2,
            Math.abs(y1 - y0) / 2,
            0,
            0,
            2 * Math.PI
          );
          ctx.stroke();
          break;
        case "oval_filled":
          ctx.beginPath();
          ctx.ellipse(
            (x0 + x1) / 2,
            (y0 + y1) / 2,
            Math.abs(x1 - x0) / 2,
            Math.abs(y1 - y0) / 2,
            0,
            0,
            2 * Math.PI
          );
          ctx.fillStyle = shapePreview.color;
          ctx.globalAlpha = 0.3;
          ctx.fill();
          ctx.globalAlpha = 0.7;
          ctx.stroke();
          break;
        case "line":
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.stroke();
          break;
        case "diamond":
          ctx.beginPath();
          ctx.moveTo((x0 + x1) / 2, y0);
          ctx.lineTo(x1, (y0 + y1) / 2);
          ctx.lineTo((x0 + x1) / 2, y1);
          ctx.lineTo(x0, (y0 + y1) / 2);
          ctx.closePath();
          ctx.stroke();
          break;
        case "diamond_filled":
          ctx.beginPath();
          ctx.moveTo((x0 + x1) / 2, y0);
          ctx.lineTo(x1, (y0 + y1) / 2);
          ctx.lineTo((x0 + x1) / 2, y1);
          ctx.lineTo(x0, (y0 + y1) / 2);
          ctx.closePath();
          ctx.fillStyle = shapePreview.color;
          ctx.globalAlpha = 0.3;
          ctx.fill();
          ctx.globalAlpha = 0.7;
          ctx.stroke();
          break;
        case "arrow":

          const baseHeadlen = 18;
          const headlen = baseHeadlen + (shapePreview.size > 8 ? (shapePreview.size * 1.2) : 0);
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.stroke();
          // Arrowhead
          const dx = x1 - x0;
          const dy = y1 - y0;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            const angle = Math.atan2(dy, dx);
            const ah1 = angle - Math.PI / 7;
            const ah2 = angle + Math.PI / 7;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1 - headlen * Math.cos(ah1), y1 - headlen * Math.sin(ah1));
            ctx.lineTo(x1 - headlen * Math.cos(ah2), y1 - headlen * Math.sin(ah2));
            ctx.closePath();
            ctx.fillStyle = shapePreview.color;
            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 0.7;
          }
          ctx.stroke();
          break;
        case "hexagon":
        case "hexagon_filled": {
          const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
          const rx = Math.abs(x1 - x0) / 2, ry = Math.abs(y1 - y0) / 2;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = Math.PI / 3 * i - Math.PI / 6;
            const px = cx + rx * Math.cos(angle);
            const py = cy + ry * Math.sin(angle);
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
          if (type === "hexagon_filled") {
            ctx.fillStyle = shapePreview.color;
            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 0.7;
          }
          ctx.stroke();
          break;
        }
        case "triangle":
          ctx.beginPath();
          ctx.moveTo((x0 + x1) / 2, y0);
          ctx.lineTo(x1, y1);
          ctx.lineTo(x0, y1);
          ctx.closePath();
          ctx.stroke();
          break;
        case "triangle_filled":
          ctx.beginPath();
          ctx.moveTo((x0 + x1) / 2, y0);
          ctx.lineTo(x1, y1);
          ctx.lineTo(x0, y1);
          ctx.closePath();
          ctx.fillStyle = shapePreview.color;
          ctx.globalAlpha = 0.3;
          ctx.fill();
          ctx.globalAlpha = 0.7;
          ctx.stroke();
          break;
        case "pentagon":
        case "pentagon_filled": {
          const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
          const r = Math.min(Math.abs(x1 - x0), Math.abs(y1 - y0)) / 2;
          ctx.beginPath();
          for (let i = 0; i < 5; i++) {
            const angle = Math.PI / 2 + (2 * Math.PI * i) / 5;
            const px = cx + r * Math.cos(angle);
            const py = cy + r * Math.sin(angle);
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
          if (type === "pentagon_filled") {
            ctx.fillStyle = shapePreview.color;
            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 0.7;
          }
          ctx.stroke();
          break;
        }
        case "star": {
          const drawStar = (cx, cy, spikes, outerRadius, innerRadius, filled = false) => {
            let rot = Math.PI / 2 * 3;
            let x = cx;
            let y = cy;
            const step = Math.PI / spikes;
            ctx.beginPath();
            ctx.moveTo(cx, cy - outerRadius);
            for (let i = 0; i < spikes; i++) {
              x = cx + Math.cos(rot) * outerRadius;
              y = cy + Math.sin(rot) * outerRadius;
              ctx.lineTo(x, y);
              rot += step;
              x = cx + Math.cos(rot) * innerRadius;
              y = cy + Math.sin(rot) * innerRadius;
              ctx.lineTo(x, y);
              rot += step;
            }
            ctx.lineTo(cx, cy - outerRadius);
            ctx.closePath();
            if (filled) {
              ctx.fillStyle = shapePreview.color;
              ctx.globalAlpha = 0.3;
              ctx.fill();
              ctx.globalAlpha = 0.7;
              ctx.stroke();
            } else {
              ctx.stroke();
            }
          };
          const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
          drawStar(x0, y0, 5, r, r / 2.5);
          break;
        }
        case "star_filled": {
          const drawStar = (cx, cy, spikes, outerRadius, innerRadius, filled = false) => {
            let rot = Math.PI / 2 * 3;
            let x = cx;
            let y = cy;
            const step = Math.PI / spikes;
            ctx.beginPath();
            ctx.moveTo(cx, cy - outerRadius);
            for (let i = 0; i < spikes; i++) {
              x = cx + Math.cos(rot) * outerRadius;
              y = cy + Math.sin(rot) * outerRadius;
              ctx.lineTo(x, y);
              rot += step;
              x = cx + Math.cos(rot) * innerRadius;
              y = cy + Math.sin(rot) * innerRadius;
              ctx.lineTo(x, y);
              rot += step;
            }
            ctx.lineTo(cx, cy - outerRadius);
            ctx.closePath();
            ctx.fillStyle = shapePreview.color;
            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 0.7;
            ctx.stroke();
          };
          const r = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
          drawStar(x0, y0, 5, r, r / 2.5, true);
          break;
        }
        default:
          break;
      }
      ctx.restore();
    }
    // --- Draw crosshair for shape tools during preview ---
    if (isDrawing && shapePreview && shapePreview.end) {
      const { end } = shapePreview;
      const x = end.x * canvas.width;
      const y = end.y * canvas.height;
      ctx.save();
      ctx.strokeStyle = '#222';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 10, y);
      ctx.lineTo(x + 10, y);
      ctx.moveTo(x, y - 10);
      ctx.lineTo(x, y + 10);
      ctx.stroke();
      ctx.restore();
    }
  }, [canvasBackground, gameState.drawingStrokes, localStroke, shapePreview, isDrawing]);

  // Pointer handlers
  const toCanvasPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x, y };
  };

  const start = (e) => {
    if (!canvasRef.current) return;
    // don't allow drawing while choosing a prompt
    if (showPrompt) return;
    if (!isDrawer) return;
    setIsDrawing(true);
    const p = toCanvasPoint(e);
    const s = {
      color: normalizeHexColor(color),
      size,
      brush: brushType,
      points: [p],
    };
    setLocalStroke(s);
  };
  const move = (e) => {
    if (!isDrawing || !localStroke) return;
    const p = toCanvasPoint(e);
    setLocalStroke((prev) => ({ ...prev, points: [...prev.points, p] }));
  };
  const end = (e) => {
    if (!isDrawing || !localStroke) return;
    // emit stroke to server normalized (points as 0..1)
      const toSend = { ...localStroke, brush: localStroke.brush || brushType };
      //if (Array.isArray(toSend.points)) {
        //console.log("[CanvasCraze] Sending brush stroke:", toSend);
      //}
    submitAction({ type: "stroke", stroke: toSend });
    setLocalStroke(null);
    setIsDrawing(false);
  };

  // --- Pointer event handlers for shape tools (using brushType) ---
  const handlePointerDown = (e) => {
  if (["rectangle", "rectangle_filled", "circle", "circle_filled", "oval", "oval_filled", "star", "star_filled", "line", "hexagon", "hexagon_filled", "triangle", "triangle_filled","diamond", "diamond_filled", "arrow", "pentagon", "pentagon_filled"].includes(brushType)) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setShapePreview({
        type: brushType,
        color,
        size,
        start: { x, y },
        end: { x, y },
      });
      setIsDrawing(true);
      return;
    }
    start(e); // fallback to original pointer down logic

  };


  const handlePointerMove = (e) => {
  if (isDrawing && shapePreview && ["rectangle", "rectangle_filled", "circle", "circle_filled", "oval", "oval_filled", "star", "star_filled", "line", "hexagon", "hexagon_filled", "triangle", "triangle_filled","diamond", "diamond_filled", "arrow", "pentagon", "pentagon_filled"].includes(brushType)) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setShapePreview((prev) => prev ? { ...prev, end: { x, y } } : null);
      return;
    }
    move(e); // fallback to original pointer move logic
  };


  const handlePointerUp = (e) => {
    if (isDrawing && shapePreview && [
      "rectangle", "rectangle_filled",
      "circle", "circle_filled",
      "oval", "oval_filled",
      "star", "star_filled",
      "hexagon", "hexagon_filled",
      "triangle", "triangle_filled",
      "diamond", "diamond_filled",
      "arrow",
      "pentagon", "pentagon_filled",
      "line"
    ].includes(brushType)) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const newShape = {
        ...shapePreview,
        end: { x, y },
      };
      // Commit the shape as a stroke to history and to the main drawingStrokes (send to server)
      // No local history/redoStack; server handles all strokes
      setShapePreview(null);
      setIsDrawing(false);
      // Send the shape as a stroke to the server so it persists in gameState.drawingStrokes
      submitAction({
        type: "stroke",
        stroke: {
          type: brushType,
          color,
          size,
          start: newShape.start,
          end: newShape.end,
        },
      });
      return;
    }
    end(e); // fallback to original pointer up logic
  };

  // Guess submission
  const submitGuess = () => {
    // only allow guesses during draw phase by non-drawers
    if (!guess) return;
    if (gameState.phase !== "draw") return;
    if (user.socketId === gameState.drawer) return;
    submitAction({ type: "guess", text: guess });
    setGuess("");
  };

  useEffect(() => {
    if (!showPrompt && !waitingOnDrawerPicking) return;
    // Use 200ms for smooth ticking
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, [showPrompt, waitingOnDrawerPicking]);

  // transiently show guesses: when gameState.guesses changes, add any new entries to visibleGuesses and remove after timeout
  useEffect(() => {
    const current = gameState.guesses || {};
    const toAdd = [];
    for (const [sid, text] of Object.entries(current)) {
      if (!visibleGuesses[sid]) toAdd.push([sid, text]);
    }
    if (toAdd.length === 0) return;
    const newVisible = { ...visibleGuesses };
    const timers = [];
    for (const [sid, text] of toAdd) {
      newVisible[sid] = text;
      // remove after 5s
      const t = setTimeout(() => {
        setVisibleGuesses((prev) => {
          const copy = { ...prev };
          delete copy[sid];
          return copy;
        });
      }, 5000);
      timers.push(t);
    }
    setVisibleGuesses(newVisible);
    return () => timers.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(gameState.guesses || {})]);

  
  // Ensure ticking interval is always active during prompt choice or draw phase
  useEffect(() => {
    const needsTick = (showPrompt || waitingOnDrawerPicking) || gameState.phase === 'draw';
    if (!needsTick) return;
    setNow(Date.now()); // force immediate update
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, [showPrompt, waitingOnDrawerPicking, gameState.phase]);


  // --- Undo/Redo logic ---
  // Undo/redo logic will be server-driven

  // Attach to Undo/Redo buttons in toolbar
  // <button onClick={handleUndo} ...>Undo</button>
  // <button onClick={handleRedo} ...>Redo</button>

  return (
    <>
      <div className="w-full h-full flex flex-col items-center p-4 dark:bg-gray-900 dark:text-white">
        <div className="w-full max-w-3xl">
          <div className="mb-2 flex flex-row items-center justify-between w-full">
            <div>
              Round {(gameState.phase === "choosePrompt" ? gameState.round + 1 : gameState.round)} / {gameState.numRounds}
            </div>
            {gameState.phase === "draw" && (
              <div className="text-sm">
                {/* Animated timer: progress bar + seconds left, driven by server timestamps */}
                <DrawTimer
                  key={gameState.roundEndsAt || `${gameState.round}-${gameState.startTime}`}
                  gameState={gameState}
                />
              </div>
            )}
            {gameState.phase === "reveal" && (
            <div className="mb-2 text-sm">
              {/* Reveal timer + revealed word for reveal phase */}
              <RevealTimer
                key={gameState.revealEndsAt || `${gameState.round}-reveal`}
                gameState={gameState}
              />
            </div>
            
    

            )}
          </div>
          {gameState.phase === "reveal" && gameState.currentWord && (
              <div className="mt-3 flex justify-center w-full">
                <div className="text-yellow-900 dark:text-yellow-100 w-full  p-2 text-center bg-yellow-50 dark:bg-blue-900 mx-auto">
                    The answer was {gameState.currentWord}
                </div>
              </div>
            )}

            {user.socketId !== gameState.drawer && gameState.phase === "draw" && (
                <div className="mt-3 flex justify-center w-full">
                  <div className="text-yellow-900 dark:text-yellow-100 w-full  p-2 text-center bg-yellow-50 dark:bg-blue-900 mx-auto">
                    <b>{drawerName}</b> is drawing... Guess the answer!
                </div>
              </div>
            )}

          {/* Show the chosen word to the drawer during draw phase */}
          {user.socketId === gameState.drawer &&
            gameState.phase === "draw" &&
            gameState.currentWord && (
              <div className="mt-3 flex justify-center w-full">
                <div className="text-yellow-900 dark:text-yellow-100 w-full  p-2 text-center bg-yellow-50 dark:bg-blue-900 mx-auto">
                  You are drawing "
                  <span className="font-bold">{gameState.currentWord}</span>"
                </div>
              </div>
            )}

          <div className="border bg-white rounded overflow-hidden relative">
            <canvas
              ref={canvasRef}
              width={600}
              height={600}
              onPointerDown={isDrawer && gameState.phase === "draw" ? handlePointerDown : undefined}
              onPointerMove={isDrawer && gameState.phase === "draw" ? handlePointerMove : undefined}
              onPointerUp={isDrawer && gameState.phase === "draw" ? handlePointerUp : undefined}
              onPointerLeave={isDrawer && gameState.phase === "draw" ? handlePointerUp : undefined}
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                maxWidth: 600,
                backgroundColor: canvasBackground,
                touchAction: "none",
                cursor: isDrawer && gameState.phase === "draw" && ["rectangle", "circle", "star", "line"].includes(brushType)
                  ? 'none'
                  : 'not-allowed'
              }}
              tabIndex={-1}
              aria-disabled={!(isDrawer && gameState.phase === "draw")}
            />
            {/* Custom dot cursor for shape tools */}
            {(isDrawing && shapePreview && [
              "rectangle", "rectangle_filled", "circle", "circle_filled", "oval", "oval_filled", "star", "star_filled", "line", "hexagon", "hexagon_filled", "triangle", "triangle_filled", "diamond", "diamond_filled", "arrow", "pentagon", "pentagon_filled"
            ].includes(brushType) && isDrawer && gameState.phase === "draw") && (
              <div
                style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 10,
                }}
                id="canvas-cursor-overlay"
              >
                <CursorDot canvasRef={canvasRef} />
              </div>
            )}




            {waitingOnDrawerPicking && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="pointer-events-auto bg-white dark:bg-gray-700 bg-opacity-95 dark:bg-opacity-95 p-4 rounded shadow-lg w-full max-w-sm mx-4">
                  <div className="mb-2 font-semibold text-center">
                    Waiting for <b>{drawerName}</b> to pick a word/phrase...
                  </div>
                </div>
              </div>
            )}

            {showPrompt && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="pointer-events-auto bg-white dark:bg-gray-700 bg-opacity-95 dark:bg-opacity-95 p-4 rounded shadow-lg w-full max-w-sm mx-4">
                  <div className="mb-2 font-semibold text-center">
                    Choose something to draw
                  </div>
                  <div className="flex flex-col items-stretch gap-2">
                    {promptChoices.map((c, i) => {
                      const text =
                        c && typeof c === "object" ? c.text : String(c || "");
                      const diff =
                        c && typeof c === "object" && c.difficulty
                          ? c.difficulty
                          : null;
                      // color code by difficulty
                      const colorClass =
                        diff === "easy"
                          ? "bg-green-600 hover:bg-green-700"
                          : diff === "medium"
                          ? "bg-orange-500 hover:bg-orange-600"
                          : diff === "hard"
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-gray-600";
                      return (
                        <button
                          key={i}
                          onClick={() => handleChoosePrompt(c, i)}
                          className={`w-full px-3 py-2 text-white rounded flex items-center justify-between ${colorClass}`}
                        >
                          <span className="truncate">{text}</span>
                          {diff && (
                            <span className="text-xs bg-white text-gray-800 px-2 ml-2 rounded lowercase">
                              {diff}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs mt-2 text-gray-600 dark:text-gray-400 text-center">
                    You have 20 seconds to pick or a prompt will be auto-selected.
                  </div>
                  {gameState.promptExpiresAt && (() => {
                    const total = 20; // seconds
                    const timeLeft = Math.max(0, (gameState.promptExpiresAt - now) / 1000);
                    const percent = Math.max(0, Math.min(1, timeLeft / total));
                    return (
                      <>
                        <div className="w-full h-2 bg-gray-300 dark:bg-gray-700 rounded overflow-hidden mt-2">
                          <div
                            className="h-2 bg-blue-500 transition-all duration-200"
                            style={{ width: `${percent * 100}%` }}
                          />
                        </div>
                        <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 text-center">
                          Time left: {Math.ceil(timeLeft)}s
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>



          <div className="mt-2 flex flex-col flex-row items-stretch sm:items-center gap-2 w-full">
            {user.socketId === gameState.drawer && gameState.phase === "draw" && (
              <>
                {/* Controls row: tool, size, preview, clear */}
                <div className="flex flex-row items-center gap-2 w-full">
                  {/* Shape dropdown */}
                  {(() => {
                    const brushOptions = [
                      { value: 'round', label: 'Brush (Round)', svg: (
                        <svg width="28" height="28"><circle cx="14" cy="14" r="9" fill="#333" className="dark:fill-gray-100"/></svg>
                      ) },
                      { value: 'square', label: 'Brush (Square)', svg: (
                        <svg width="28" height="28"><rect x="10" y="6" width="6" height="16" fill="#333" className="dark:fill-gray-100"/></svg>
                      ) },
                      { value: 'marker', label: 'Marker', svg: (
                        <svg width="28" height="28"><rect x="7" y="10" width="14" height="8" rx="4" fill="#333" opacity="0.5" className="dark:fill-gray-100"/></svg>
                      ) },
                    ];
                    const shapeOptions = [
                      // Unfilled shapes
                      { value: 'line', label: 'Line', svg: (<svg width="28" height="28"><line x1="4" y1="14" x2="24" y2="14" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'arrow', label: 'Arrow', svg: (<svg width="28" height="28"><line x1="4" y1="14" x2="20" y2="14" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/><polygon points="20,10 27,14 20,18" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'rectangle', label: 'Rectangle', svg: (<svg width="28" height="28"><rect x="4" y="7" width="20" height="14" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'oval', label: 'Oval', svg: (<svg width="28" height="28"><ellipse cx="14" cy="14" rx="10" ry="6" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'circle', label: 'Circle', svg: (<svg width="28" height="28"><circle cx="14" cy="14" r="9" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'diamond', label: 'Diamond', svg: (<svg width="28" height="28"><polygon points="14,4 24,14 14,24 4,14" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      
                      { value: 'hexagon', label: 'Hexagon', svg: (<svg width="28" height="28"><polygon points="7,6 21,6 27,14 21,22 7,22 1,14" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'triangle', label: 'Triangle', svg: (<svg width="28" height="28"><polygon points="14,4 24,24 4,24" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'pentagon', label: 'Pentagon', svg: (<svg width="28" height="28"><polygon points="14,4 25,12 20,25 8,25 3,12" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      { value: 'star', label: 'Star', svg: (<svg width="28" height="28" viewBox="0 0 28 28"><polygon points="14,4 17,12 26,12 18.5,17 21,25 14,20 7,25 9.5,17 2,12 11,12" fill="none" stroke="#333" strokeWidth="2" className="dark:stroke-gray-100"/></svg>) },
                      // Filled shapes
                      { value: 'rectangle_filled', label: 'Rectangle (filled)', svg: (<svg width="28" height="28"><rect x="4" y="7" width="20" height="14" fill="#333" className="dark:fill-gray-100" stroke="#333" strokeWidth="2"/></svg>) },
                      { value: 'oval_filled', label: 'Oval (filled)', svg: (<svg width="28" height="28"><ellipse cx="14" cy="14" rx="10" ry="6" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'circle_filled', label: 'Circle (filled)', svg: (<svg width="28" height="28"><circle cx="14" cy="14" r="9" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'diamond_filled', label: 'Diamond (filled)', svg: (<svg width="28" height="28"><polygon points="14,4 24,14 14,24 4,14" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      
                      { value: 'hexagon_filled', label: 'Hexagon (filled)', svg: (<svg width="28" height="28"><polygon points="7,6 21,6 27,14 21,22 7,22 1,14" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'triangle_filled', label: 'Triangle (filled)', svg: (<svg width="28" height="28"><polygon points="14,4 24,24 4,24" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'pentagon_filled', label: 'Pentagon (filled)', svg: (<svg width="28" height="28"><polygon points="14,4 25,12 20,25 8,25 3,12" fill="#333" className="dark:fill-gray-100"/></svg>) },
                      { value: 'star_filled', label: 'Star (filled)', svg: (<svg width="28" height="28" viewBox="0 0 28 28"><polygon points="14,4 17,12 26,12 18.5,17 21,25 14,20 7,25 9.5,17 2,12 11,12" fill="#333" className="dark:fill-gray-100"/></svg>) },
                    ];
                    return (
                      <div style={{ position: 'relative', minWidth: 36 }}>
                        <button
                          type="button"
                          className="border rounded flex items-center justify-center transition-colors duration-100 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          style={{ width: 36, height: 36, padding: 0 }}
                          aria-label="Shape visual"
                          onClick={() => {
                            setShowShapeDropdown(v => !v);
                            setShowBrushDropdown(false);
                          }}
                        >
                          {
                            (() => {
                              const allOptions = [...brushOptions, ...shapeOptions];
                              return allOptions.find(opt => opt.value === brushType)?.svg || brushOptions[0].svg;
                            })()
                          }
                        </button>
                        {showShapeDropdown && (
                          <div
                            className="shadow-lg border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-800"
                            style={{
                              position: 'absolute',
                              top: '110%',
                              left: 0,
                              zIndex: 20,
                              minWidth: 180,
                              width: 220,
                              padding: 8
                            }}
                            onMouseLeave={() => setShowShapeDropdown(false)}
                          >
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 ml-1">Brushes</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 8 }}>
                              {brushOptions.map(s => (
                                <button
                                  key={s.value}
                                  type="button"
                                  className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-75 ${brushType === s.value ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900' : 'border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'} hover:bg-blue-100 dark:hover:bg-blue-800`}
                                  aria-label={s.label}
                                  onClick={() => { setBrushType(s.value); setShowShapeDropdown(false); }}
                                >
                                  {s.svg}
                                </button>
                              ))}
                            </div>
                            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 ml-1 mt-2">Shapes</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                              {shapeOptions.map(s => (
                                <button
                                  key={s.value}
                                  type="button"
                                  className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-75 ${brushType === s.value ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900' : 'border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'} hover:bg-blue-100 dark:hover:bg-blue-800`}
                                  aria-label={s.label}
                                  onClick={() => { setBrushType(s.value); setShowShapeDropdown(false); }}
                                >
                                  {s.svg}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Custom brush size dropdown with label */}
                  <div className="flex flex-col items-center" style={{ minWidth: 36 }}>
                    
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="border rounded flex items-center justify-center transition-colors duration-100 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        style={{ width: 36, height: 36, padding: 0 }}
                        aria-label="Brush size visual"
                        onClick={() => {
                          setShowBrushDropdown(v => !v);
                          setShowShapeDropdown(false);
                        }}
                      >
                        {brushSizes.find(b => b.value === size)?.svg}
                      </button>
                      {showBrushDropdown && (
                        <div
                          className="shadow-lg border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-800"
                          style={{
                            position: 'absolute',
                            top: '110%',
                            left: 0,
                            zIndex: 20,
                            minWidth: 200,
                            width: 240,
                            padding: 8
                          }}
                          onMouseLeave={() => setShowBrushDropdown(false)}
                        >
                          <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 ml-1">Brush Size</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                            {brushSizes.map(b => (
                              <button
                                key={b.value}
                                type="button"
                                className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-75 ${b.value === size ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900' : 'border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'} hover:bg-blue-100 dark:hover:bg-blue-800`}
                                aria-label={`Brush size ${b.value}`}
                                onClick={() => { setSize(b.value); setShowBrushDropdown(false); }}
                              >
                                {b.svg}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => submitAction({ type: "setCanvasBackground", color })}
                    className="flex items-center justify-center rounded border border-gray-300 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: color,
                      color: (() => {
                        // If color is white or near-white, use dark text
                        const c = color.toLowerCase();
                        return (c === '#fff' || c === '#ffffff' || c === '#ffffff00' || c === '#ffffffff') ? '#222' : '#fff';
                      })(),
                      border: '1px solid #bbb',
                    }}
                    aria-label="Set canvas background"
                    title="Set canvas background"
                  >BG
                  </button>
                  <button
                    onClick={() => submitAction({ type: 'undo' })}
                    className="flex items-center justify-center bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded w-full sm:w-auto mr-1"
                    style={{ width: 36, height: 36 }}
                    aria-label="Undo"
                  >
                    {/* Undo icon */}
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 6L3 11L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 11H13C16.3137 11 19 13.6863 19 17V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => setShowClearModal(true)}
                    className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded w-full sm:w-auto transition-colors"
                    style={{ width: 36, height: 36 }}
                    aria-label="Clear"
                  >
                    {/* White cross icon on red background */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <line x1="5" y1="5" x2="15" y2="15" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                      <line x1="15" y1="5" x2="5" y2="15" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                    </svg>
                  </button>

                </div>

                {/* Color picker and palette always on a new row below controls, never beside */}
                <div className="w-full flex flex-row items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(normalizeHexColor(e.target.value))}
                    style={{
                      borderRadius: '20%',
                      width: 52,
                      height: 56,
                      padding: 0,
                      border: 'none',
                      boxShadow: '0 0 0 0px #2196f3',
                      display: 'block',
                      outline: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  />
                  <div className="flex flex-col gap-1">

                    {[
                      [
                        '#000000', '#444444', '#888888', '#CCCCCC', '#FFFFFF',
                        '#FF0000', '#FF8000', '#FFFF00', '#80FF00', '#00FF00'
                      ],
                      [
                        '#00FF80', '#00FFFF', '#0080FF', '#0000FF', '#8000FF',
                        '#FF00FF', '#FF0080', '#804000', '#808000', '#008080'
                      ]
                    ].map((row, rowIdx) => (
                      <div key={rowIdx} className="flex flex-row gap-1">
                        {row.map((preset, i) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setColor(normalizeHexColor(preset))}
                            style={{
                              background: preset,
                              width: 24,
                              height: 24,
                              borderRadius: 6,
                              border: color.toLowerCase() === normalizeHexColor(preset).toLowerCase() ? '2px solid #333' : '1px solid #bbb',
                              outline: 'none',
                              cursor: 'pointer',
                              margin: 0,
                              boxShadow: color.toLowerCase() === normalizeHexColor(preset).toLowerCase() ? '0 0 0 2px #2196f3' : undefined
                            }}
                            aria-label={`Set color ${preset}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}



          </div>



          {gameState.phase === "draw" && (
            <div className="mt-3">
              <div className="text-sm font-semibold mb-1">Guesses</div>
              <div className="space-y-1">
                {Object.entries(gameState.guesses || {}).map(([sid, text]) => {
                  const player = (gamePlayers || []).find(
                    (p) => p.socketId === sid
                  ) || { name: sid };
                  const displayName = player.username || player.name || sid;
                  const isCorrect = (gameState.correctGuessers || []).includes(
                    sid
                  );
                  return (
                    <div
                      key={sid}
                      className={`px-2 py-1 rounded ${
                        isCorrect
                          ? "bg-green-200 dark:bg-green-700"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}
                    >
                      <span className="font-semibold mr-2">{displayName}:</span>
                      {isCorrect ? (
                        <span className="italic">guessed the word correctly</span>
                      ) : (
                        <span>{text}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {gameState.phase === "draw" && user.socketId !== gameState.drawer && (
            <>
              {gameState.closeGuesses && gameState.closeGuesses[user.socketId] && (
                <div className="mb-2 text-orange-600 font-bold text-center animate-pulse">Close!! Try again</div>
              )}
              <form
                className="mt-4 flex"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitGuess();
                }}
              >
                <input
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  placeholder="Type your guess"
                  className="border p-2 flex-1 mr-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400"
                />
                <button
                  type="submit"
                  className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 dark:bg-blue-700 dark:hover:bg-blue-600"
                >
                  Submit Guess
                </button>
              </form>
            </>
          )}
          {gameState.phase === "draw" && user.socketId === gameState.drawer && (
            <></>
          )}
        </div>
      </div>
      {/* Clear confirmation modal */}
      <Modal
        show={showClearModal}
        title="Clear Drawing?"
        onClose={() => setShowClearModal(false)}
      >
        <div className="p-4">
          <div className="mb-4 text-lg">Are you sure you want to clear the drawing?</div>
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={() => setShowClearModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
              onClick={() => {
                setShowClearModal(false);
                submitAction({ type: "clearCanvas" });
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}


// Animated draw timer component: shows a progress bar and seconds left using requestAnimationFrame.
function DrawTimer({ gameState }) {
  const [secsLeft, setSecsLeft] = useState(null);

  useEffect(() => {
    const endsAt =
      gameState.roundEndsAt ||
      (gameState.startTime
        ? gameState.startTime + (gameState.timeLimit || 60) * 1000
        : null);
    // const timeLimit = gameState.timeLimit || 60; // unused
    if (!endsAt) {
      setSecsLeft(null);
      return;
    }
    let raf = null;
    // const start = endsAt - timeLimit * 1000; // unused
    const tick = () => {
      const now = Date.now();
      const msLeft = Math.max(0, endsAt - now);
      setSecsLeft(Math.ceil(msLeft / 1000));
      if (msLeft > 0) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [gameState.roundEndsAt, gameState.startTime, gameState.timeLimit]);

  if (secsLeft === null) return null;

  return (
    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right w-full">
      Round ends in {secsLeft}s 
    </div>
  );
}

// Reveal timer component: similar to DrawTimer but uses revealEndsAt and shows the revealed word
function RevealTimer({ gameState }) {
  const [secsLeft, setSecsLeft] = useState(null);

  useEffect(() => {
    const endsAt = gameState.revealEndsAt || null;
    if (!endsAt) {
      setSecsLeft(null);
      return;
    }
    let raf = null;
    // const duration = Math.max(0, endsAt - Date.now()); // unused
    // const start = endsAt - duration; // unused
    const tick = () => {
      const now = Date.now();
      const msLeft = Math.max(0, endsAt - now);
      setSecsLeft(Math.ceil(msLeft / 1000));
      if (msLeft > 0) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [gameState.revealEndsAt]);

  if (secsLeft === null) return null;

  return (
    <>
      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right w-full">
        
        Next round in {secsLeft}s
      </div>
      
    </>
  );
}

// Ensure color is a #RRGGBB hex string. If an 8-digit hex (#RRGGBBAA) is passed, drop the alpha.
function normalizeHexColor(c) {
  if (!c || typeof c !== "string") return "#007bff";
  if (c.startsWith("#")) {
    if (c.length === 9) {
      // #RRGGBBAA -> strip alpha
      return `#${c.slice(1, 7)}`;
    }
    if (c.length === 7) return c;
    if (c.length === 4) {
      // #RGB -> expand to #RRGGBB
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
  }
  return c;
}

// Custom dot cursor overlay for shape tools
// Must be at the top level, not inside any function
function CursorDot({ canvasRef }) {
  const [pos, setPos] = useState({ x: null, y: null });
  useEffect(() => {
    const handleMove = (e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      setPos({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    };
    window.addEventListener('pointermove', handleMove);
    return () => window.removeEventListener('pointermove', handleMove);
  }, [canvasRef]);
  if (pos.x === null || pos.y === null) return null;
  return (
    <div
      style={{
        position: 'absolute',
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: 10,
        height: 10,
        background: '#222',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 100,
        border: '2px solid #fff',
      }}
    />
  );
}
  
CanvasCrazeGame.Guide = function CanvasCrazeGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Canvas Craze</h3>
      <p>
        One player draws while others guess. The drawer chooses a word or phrase and has a limited time to draw. The first correct guess scores most points.
      </p>
    </div>
  );
};

export default CanvasCrazeGame;
